import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ConvertHelper } from '@barthauer/abc/core';
import {
  DataService,
  ObjectTypeInfoColumnViewModel,
  ObjectTypeInfoReferenceViewModel,
  ObjectTypeInfoReferencingViewModel,
  ObjectTypeInfoViewModel,
  QueryExpressionModel,
} from '@barthauer/abc/core/data';
import { ParameterDefinition, ParameterValue } from '@barthauer/abc/queries/parameters/core';
import {
  AbcTextBoxComponent,
  _AbcScrollViewComponent,
  _AbcSplitterComponent,
  _AbcTreeNode,
} from '@barthauer/abc/ui-components';

import { ValueChangedEvent } from 'devextreme/ui/text_box';
import {
  EditorInstanceSettings,
  ExpressionEditorResult,
  ValidationResult,
  ValidationType
} from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EntityComponent } from './core/entity/entity.component';
import { FunctionComponent } from './core/function/function.component';
import { MenuComponent } from './core/menu/menu.component';
import { NavigationComponent } from './core/navigation/navigation.component';
import { OperatorsComponent } from './core/operators/operators.component';
import { SearchComponent } from './core/search/search.component';
import { ValidateComponent } from './core/validate/validate.component';
import { AbcExpressionsEditorService } from './expressions-editor.service';
import { SelectedEntity } from './models/entity.model';
import { CursorBackUp, EditorTitle, ExpressionHistory } from './models/expressions-editor-opath.model';
import { OPathFunction, OPathFunctionCategory, OPathFunctions } from './models/function.model';
import { MenuButtonEnum } from './models/menu.model';
import { ComponentNameEnum } from './models/navigation.model';
import { OPathOperator, OperatorPosition } from './models/operators.model';
import { MatchMode, REGEX_FLAG, RegExpMode, ReplaceMode, SearchParameter } from './models/search.model';
import { Splitter, SplitterEnum, TranslationConfiguration, UiConfig, UiEnum, UiSettings } from './models/view.model';
import { EditorService } from './services/editor.service';
import { FunctionService } from './services/function.service';
import { OPathValidationService } from './services/oPath-validation.service';
import { OperatorsService } from './services/operators.service';
import { SharedService } from './services/shared.service';

@Component({
    selector: 'expressions-editor',
    templateUrl: './expressions-editor.component.html',
    styleUrls: ['./expressions-editor.component.scss'],
    providers: [AbcExpressionsEditorService],
    standalone: false
})
export class ExpressionsEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('menuComponent') menuComponent: MenuComponent;
  @ViewChild('layoutContainer', { static: false }) layoutContainertRef: ElementRef<HTMLElement>;
  @ViewChild('layoutEditorContainer', { static: false }) layoutEditorContainerRef: ElementRef<HTMLElement>;
  @ViewChild('navigationContainer', { static: false }) navigationContainerRef: ElementRef<HTMLElement>;
  @ViewChild('splitterEntity', { static: false }) splitterEntity: _AbcSplitterComponent;
  @ViewChild('splitterFunction', { static: false }) splitterFunction: _AbcSplitterComponent;
  @ViewChild('splitterParameter', { static: false }) splitterParameter: _AbcSplitterComponent;
  @ViewChild('operatorsLeftPosition', { static: false }) operatorsLeftPositionRef: OperatorsComponent;
  @ViewChild('columnTitleTextBox', { static: false }) columnTitleTextBoxRef: AbcTextBoxComponent;
  @ViewChild('editor') editor: ElementRef<HTMLElement>;
  @ViewChild(EntityComponent) entityComponent: EntityComponent;
  @ViewChild(ValidateComponent) validateComponent: ValidateComponent;
  @ViewChild(SearchComponent) searchComponent: SearchComponent;
  @ViewChild(FunctionComponent) functionComponent: FunctionComponent;
  @ViewChild(OperatorsComponent) operatorComponent: OperatorsComponent;
  @ViewChild('navigation') navComponent: NavigationComponent;
  @ViewChild('editorScrollView') editorScrollView: _AbcScrollViewComponent;
  private _objectTypeInfos: ObjectTypeInfoViewModel[];

  public set objectTypeInfos(objectTypeInfos: ObjectTypeInfoViewModel[]) {
    this._objectTypeInfos = objectTypeInfos;
    this.objectTypeInfo = objectTypeInfos[objectTypeInfos.length - 1];
  }

  public get objectTypeInfos(): ObjectTypeInfoViewModel[] {
    return this._objectTypeInfos;
  }

  private _objectTypeInfo: ObjectTypeInfoViewModel;
  public set objectTypeInfo(objectTypeInfo: ObjectTypeInfoViewModel) {
    this._objectTypeInfo = objectTypeInfo;
  }

  public get objectTypeInfo(): ObjectTypeInfoViewModel {
    return this._objectTypeInfo;
  }

  private _oPathExpressionValue: string;
  @Input()
  public set oPathExpressionValue(value: string) {
    this._oPathExpressionValue = value;
  }

  public get oPathExpressionValue(): string {
    return this._oPathExpressionValue;
  }

  private _validationType: ValidationType;
  @Input()
  public set validationType(validationType: ValidationType) {
    this._validationType = validationType;
  }

  public get validationType(): ValidationType {
    return this._validationType;
  }

  @Output() onCloseClick = new EventEmitter<void>();
  public ComponentNameEnum = ComponentNameEnum;
  public UiEnum = UiEnum;
  public UiSettings = UiSettings;
  public SplitterEnum = SplitterEnum;
  public splitter: Map<SplitterEnum, Splitter> = new Map<SplitterEnum, Splitter>();
  public uiConfig: Map<UiEnum, UiConfig> = new Map<UiEnum, UiConfig>();

  public parameterDefinitions: ParameterDefinition[] = [];
  public editorLineCount: number = 0;
  // public showNavMenu: boolean = true;
  // public showFunctionsPermanently: boolean = false;
  // public showEntityPermanently: boolean = true;
  public permanentNavigation: {
    [key in ComponentNameEnum] : boolean;
  } = {
    [ComponentNameEnum.EntityComponent]: true,
    [ComponentNameEnum.FunctionComponent]: false,
    [ComponentNameEnum.ParametersComponent]: false,
    [ComponentNameEnum.SearchComponent]: false,
    [ComponentNameEnum.ValidateComponent]: false,
    [ComponentNameEnum.OperatorsComponent]: false,
    [ComponentNameEnum.OperatorLeftPosition]: false,
    [ComponentNameEnum.OperatorBottomPosition]: false
  }
  public searchTextIndex: number = null;
  public serchResultNumber: number = null;
  public searchOccurence: number = 0;
  public lineNumbers: number[] = [1];
  public matcheIndex: number = 0;
  public mouseTextSelected: string = '';
  public _navigationComponent: ComponentNameEnum;
  set navigationComponent(value: ComponentNameEnum) {
    if (this._navigationComponent !== value) {
      this._navigationComponent = value;
    }
  }
  get navigationComponent(): ComponentNameEnum {
    return this._navigationComponent;
  }
  public oPathOperators: OPathOperator[] = [];
  public oPathEditor: HTMLElement;
  public currentOperatorPosition: OperatorPosition = OperatorPosition.right;
  public OperatorPosition = OperatorPosition;
  public history:ExpressionHistory = new ExpressionHistory();
  public validationResult: ValidationResult;
  public neutralDesignationActive: boolean;
  public editorInstancesettings: EditorInstanceSettings;

  public columnTitle: EditorTitle = new EditorTitle()

  public translation: TranslationConfiguration;
  public editorInitialValue: string = '';
  private currentSearch: string = '';
  private isAllMatchActive: boolean = true;
  private editorContentArray: string[] = [];
  private oPathFunctions: OPathFunction[] = [];
  private regex: RegExp;
  private result: ExpressionEditorResult;
  private saveCloseButtonClicked: boolean = false;
  private selectedRegExp: string[] = [];
  private unsubscribe$: Subject<void> = new Subject<void>();
  private cursorPositionBackup: CursorBackUp = null;
  public navigation: Map<ComponentNameEnum, { active: () => boolean }> = new  Map<ComponentNameEnum, { active: () => boolean }>;

  constructor(
    private functionService: FunctionService,
    private oPathValidationService: OPathValidationService,
    private expressionEditorService: AbcExpressionsEditorService,
    private operatorsService: OperatorsService,
    private coreDataService: DataService,
    private editorService: EditorService,
    private sharedService: SharedService
  ) {
    this.permanentNavigation[ComponentNameEnum.EntityComponent] = true
    this.navigationComponent = ComponentNameEnum.EntityComponent;
    this.editorInstancesettings = this.editorService.editorInstances[this.editorService.editorInstances.length - 1];
    this.translation = this.sharedService.translation;
    this.initiatlizeSplitters();
    this.initializeUiSize();
    this.initializeNavigation()
  }

  private initializeNavigation(): void {
    const navigationConditions = [
      { name: ComponentNameEnum.EntityComponent, condition: () => this.navigationComponent === ComponentNameEnum.EntityComponent || this.permanentNavigation[ComponentNameEnum.EntityComponent] },
      { name: ComponentNameEnum.FunctionComponent, condition: () => this.navigationComponent === ComponentNameEnum.FunctionComponent || this.permanentNavigation[ComponentNameEnum.FunctionComponent] },
      { name: ComponentNameEnum.ParametersComponent, condition: () => this.navigationComponent === ComponentNameEnum.ParametersComponent },
      { name: ComponentNameEnum.SearchComponent, condition: () => this.navigationComponent === ComponentNameEnum.SearchComponent },
      { name: ComponentNameEnum.ValidateComponent, condition: () => this.navigationComponent === ComponentNameEnum.ValidateComponent },
      { name: ComponentNameEnum.OperatorsComponent, condition: () => this.navigationComponent === ComponentNameEnum.OperatorsComponent && this.currentOperatorPosition === OperatorPosition.right },
      { name: ComponentNameEnum.OperatorLeftPosition, condition: () => this.currentOperatorPosition === OperatorPosition.left },
      { name: ComponentNameEnum.OperatorBottomPosition, condition: () => this.currentOperatorPosition === OperatorPosition.bottom }
    ];
    
    navigationConditions.forEach(({ name, condition }) => {
      this.navigation.set(name, { active: condition });
    });
  }

  private initializeUiSize(): void {
    this.uiConfig.set(UiEnum.operator, {
      height: () => this.handleOperatorsHeight(),
      width: () => this.handleOperatorsWidth(),
    });

    const editorUiConfigs = [
      { ui: UiEnum.editorContainer, width: () => this.editorWidth(UiEnum.editorContainer) },
      { ui: UiEnum.editorTitle, width: () => this.editorWidth(UiEnum.editorTitle) },
      { ui: UiEnum.editorBody, width: () => this.editorWidth(UiEnum.editorBody) }
    ];
  
    editorUiConfigs.forEach(config => {
      this.uiConfig.set(config.ui, {
        width: config.width
      });
    });
  }
  private initiatlizeSplitters(): void {
    const navigation = [SplitterEnum.entity, SplitterEnum.function, SplitterEnum.parameter]

    navigation.forEach( (splitterEnum : SplitterEnum)=>   this.splitter.set(splitterEnum, {
      maxWidth: () => this.handleSplitterMaxWidth(splitterEnum),
    }))
  }

  public getNavigation(navName: ComponentNameEnum): boolean {
    return this.navigation.get(navName).active();
  }

  public async ngOnInit(): Promise<void> {
    this.neutralDesignationActive =
      (await this.editorService.getValue(new ParameterValue('neutralDesignationActive')).toPromise()) === 'False'
        ? false
        : true;

    if (this.oPathExpressionValue) {
      const translatedOpath = ConvertHelper.toString(await this.translateOpath(this.oPathExpressionValue, true));
      translatedOpath.length && (this.oPathExpressionValue = translatedOpath);
    }

    this.editorInitialValue = this.oPathExpressionValue;

    const setOpathFunctions = (oPathFunctions: OPathFunctions): void => {
      oPathFunctions.Categories.forEach((oPathFunctionCategory: OPathFunctionCategory) => {
        oPathFunctionCategory.Functions.forEach((oPathFunction: OPathFunction) => {
          this.oPathFunctions.push(oPathFunction);
        });
      });
    };

    if (this.functionService.oPathFunctions) {
      setOpathFunctions(this.functionService.oPathFunctions);
    } else {
      this.functionService
        .createReadFunctionInfo()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((oPathFunctions: OPathFunctions) => {
          setOpathFunctions(oPathFunctions);
        });
    }

    this.oPathOperators = this.operatorsService.oPathOperators;

    this.expressionEditorService.onValidationResult
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async (validationResult: ValidationResult) => {
        this.validationResult = validationResult;
        if (
          (this.validationType === ValidationType.Query && !validationResult.RefObjectTypeName) ||
          (this.validationType === ValidationType.Column && !validationResult.ValueCount)
        ) {
          this.navigateAndStartValidation();
        } else if (this.saveCloseButtonClicked) {
          this.saveCloseButtonClicked = false;
          const translatedOpath = ConvertHelper.toString(
            await this.translateOpath(this.editor.nativeElement.innerText)
          );
          this.createResult(
            this.neutralDesignationActive
              ? this.editor.nativeElement.innerText
              : translatedOpath.length
              ? translatedOpath
              : this.editor.nativeElement.innerText,
            this.parameterDefinitions,
            validationResult
          );
          this.onCloseClick.emit();
        } else {
          this.navigateAndStartValidation();
        }
      });
  }
  async ngAfterViewInit(): Promise<void> {
    this.columnTitle = {
      initialValue: this.columnTitleTextBoxRef?.value,
      currentValue: '',
    };
    this.oPathEditor = document.getElementById(this.editorInstancesettings.editorId);
    setTimeout(() => {
      this.oPathEditor = document.getElementById(this.editorInstancesettings.editorId);
      this.editorInitialValue && (this.oPathEditor.innerHTML = this.editorInitialValue);
      this.highlightInvalidParameters();
    }, 1000);
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateLineNumber();
  }

  @HostListener('window:keydown', ['$event'])
  public handleKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.key === 'f') {
      const SEARCHCOMP_IS_LOADED = this.navigationComponent === ComponentNameEnum.SearchComponent;
      event.preventDefault();
      this.oPathEditor.blur();

      if (SEARCHCOMP_IS_LOADED) {
        this.searchComponent.header.searchInput.instance.focus();
        if (this.mouseTextSelected !== null && this.searchComponent.header.searchInput) {
          this.searchComponent.header.searchInput.value = this.mouseTextSelected;
        }
      } else {
        this.navComponent.loadComponent({
          componentName: ComponentNameEnum.SearchComponent,
          icon: 'search',
          toolTip: 'Search',
          isSelected: true,
        });
      }
    }
    if (event.ctrlKey && (event.key === 's' || event.key === 'z')) {
      event.preventDefault();
    }
  }

  @HostListener('window:mousedown', ['$event'])
  handleMouseSelection(event: MouseEvent): void {
    this.mouseTextSelected = '';
    const target = (event.target as HTMLElement).className;
    const isTargetValid = target.includes('editor') || target.includes('scrollable');
    if (!isTargetValid) {
      event.preventDefault();
    }
  }

  public setcolumnTitle(event: ValueChangedEvent) {
    this.columnTitle.currentValue = event.value;
  }

  public onPaste(event: ClipboardEvent) {
    if (this.navigationComponent === 'SearchComponent') {
      if (this.searchComponent.currentSearchText.length > 0) {
        return;
      }
    }
    if (this.isEditorSelected) {
      event.preventDefault();
      event.stopPropagation();
      const text = (event.clipboardData || window['clipboardData']).getData('text');
      this.insertContentToEditor(text, null);
    }
  }

 

  private placeCaretAtTheEnd(): void {
    const element: HTMLElement = this.editor.nativeElement;
    element.focus();

    const range: Range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);

    const selection: Selection = window.getSelection();

    if (this.oPathEditor.innerText.length) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    this.backupCursorPosition(selection);
  }

  public changeOperatorsPosition(event: OperatorPosition): void {
    this.currentOperatorPosition = event;
    this.updateLineNumber();
  }

  public getResult(): ExpressionEditorResult {
    return this.result;
  }

  public handleParameterSelected(parameterName: string) {
    if (parameterName) {
      const parameterCaption = '$' + parameterName;
      this.insertContentToEditor('' + parameterCaption, ComponentNameEnum.ParametersComponent);
    }
  }

  public highlightInvalidParameters() {
    const regex = /\$[a-zA-Z0-9]+/gi;
    const editorParameters = this.oPathEditor.innerHTML.match(regex);
  
    if (editorParameters) {
      let innerHTML = this.oPathEditor.innerHTML;
      editorParameters.forEach((editorParameter) => {
        if (
          !this.parameterDefinitions.find(
            (parameterDefinitions: ParameterDefinition) =>
              `$${parameterDefinitions.Name}`.toLocaleLowerCase() === editorParameter.toLocaleLowerCase()
          )
        ) {
          const invalidRegex = new RegExp(editorParameter.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
          innerHTML = innerHTML.replace(invalidRegex, `<span style="color: red;">${editorParameter}</span>`);
        }
      });
      this.oPathEditor.innerHTML = innerHTML;
    }
    this.placeCaretAtTheEnd();
    this.updateLineNumber();
  }

  public handleEntitySelection(selectedEntity: SelectedEntity): void {
    if (selectedEntity.isCTRLKeyHold) {
      if (selectedEntity.parentObjectInfo) {
        this.insertContentToEditor(
          this.createSelectedEntityPath(this.entityComponent.entityTree.getNodeById(selectedEntity.id)),
          ComponentNameEnum.EntityComponent
        );
      } else if (selectedEntity.isReferencing) {
        this.insertContentToEditor(
          (this.entityComponent.neutralDesignationActive
            ? selectedEntity.objectInfo.name
            : selectedEntity.objectInfo.displayText) + '[<Filter>]',
          ComponentNameEnum.EntityComponent
        );
      } else {
        this.insertContentToEditor(
          this.entityComponent.neutralDesignationActive
            ? selectedEntity.objectInfo.name
            : selectedEntity.objectInfo.displayText,
          ComponentNameEnum.EntityComponent
        );
      }
    } else {
      this.insertContentToEditor(
        this.entityComponent.neutralDesignationActive
          ? selectedEntity.objectInfo.name
          : selectedEntity.objectInfo.displayText,
        ComponentNameEnum.EntityComponent
      );
    }
  }

  private createSelectedEntityPath(treeNode: _AbcTreeNode): string {
    let pathArray: string[] = [];
    let isReferencing: boolean;

    const buildpath = (treeNode: _AbcTreeNode) => {
      isReferencing = treeNode.data instanceof ObjectTypeInfoReferencingViewModel;

      const nodeData = isReferencing
        ? (treeNode.data as ObjectTypeInfoReferencingViewModel)
        : (treeNode.data as ObjectTypeInfoReferenceViewModel | ObjectTypeInfoColumnViewModel);

      const fragment = this.neutralDesignationActive ? nodeData.name : nodeData.displayText;

      const pushFragment = () => pathArray.push(isReferencing ? fragment + '[<filter>]' : fragment + '');

      if (treeNode.parentNode && treeNode.parentNode !== this.entityComponent.rootTreeNodes[0]) {
        pushFragment();
        buildpath(treeNode.parentNode);
      } else {
        pushFragment();
      }
    };

    buildpath(treeNode);

    return pathArray.reverse().join('.');
  }

  public handleMenuButtonClick(menuButton: MenuButtonEnum) {
    switch (menuButton) {
      case MenuButtonEnum.SaveClose:
        this.handleSaveCloseButtonClick();
        break;
      case MenuButtonEnum.Reset:
        this.handleResetButtonClick();
        break;
      case MenuButtonEnum.Undo:
        this.handleUndoButtonClick();
        break;
      case MenuButtonEnum.Redo:
        this.handleRedoButtonClick();
        break;
      case MenuButtonEnum.Validate:
        this.handleValidateButtonClick();
        break;
      case MenuButtonEnum.ToggleNeutralDesignation:
        this.handleToggleNeutralDesignationButtonClick();
        break;
      default:
        break;
    }
  }

  public handleOperatortSelected(oPathOperator: OPathOperator) {
    this.insertContentToEditor(
      oPathOperator.identifier === 'In'
        ? ' ' + oPathOperator.identifier + '(...) '
        : ' ' + oPathOperator.identifier + ' ',
      ComponentNameEnum.OperatorsComponent
    );
  }

  public handleFunctionSelected(oPathFunction: OPathFunction) {
    let parameter = '';
    if (oPathFunction.Parameters) {
      oPathFunction.Parameters.map((functionParametre) => (parameter += '<' + functionParametre.name + '>' + ', '));

      const newContent = oPathFunction.name + '(' + parameter.slice(0, -2) + ')';
      this.insertContentToEditor(newContent, ComponentNameEnum.FunctionComponent);
      this.updateLineNumber();
    }
  }

  public handleKeyboardChange(event: KeyboardEvent): void {
    const undoButtonDisabled = this.menuComponent?.undoButton?.disabled;
    const redoButtonDisabled = this.menuComponent?.redoButton?.disabled;
    if (event.ctrlKey) {
      if (event.key === 'z' && !undoButtonDisabled) {
        this.handleUndoButtonClick();
        this.oPathEditor.blur();
        if (this.history.undo.length === 0) {
          this.menuComponent.undoButton.disabled = true;
        }
      } else if (event.key === 'y' && !redoButtonDisabled) {
        this.handleRedoButtonClick();
        this.oPathEditor.blur();
        if (this.history.undo.length === 0) {
          this.menuComponent.redoButton.disabled = true;
        }
      } else if (event.key === 'v') {
        this.saveEditorHistory();
        //MOH : 10 mlseconds timeout to wait for the paste to complete before searching to ensur that the paste is  taken in consideration
        setTimeout(() => this.triggerSearchOnEditorChange(), 10);
      }
    }

    this.updateLineNumber();
    this.backupCursorPosition();
  }

  public handleOperatorsHeight() {
    return (this.height = this.layoutEditorContainerRef.nativeElement.clientHeight - 35);
  }

  height: number = 0;
  public handleOperatorsWidth(): number {
    switch (true) {
      case this.height <= 620 && this.height >= 345:
        return 80;
      case this.height < 345:
        return 120;
      default:
        return 40;
    }
  }
  public handleSplitterMaxWidth(splitter: SplitterEnum): number {
    const navvWidth = this.navigationContainerRef?.nativeElement?.clientWidth ?? 0;
    const layoutContainerWidth = this.layoutContainertRef?.nativeElement?.clientWidth;
    const splitterWidths = {
      [SplitterEnum.entity]: (this.splitterEntity?.width as number) ?? 0,
      [SplitterEnum.function]: (this.splitterFunction?.width as number) ?? 0,
      [SplitterEnum.parameter]: (this.splitterParameter?.width as number) ?? 0,
    };

    return layoutContainerWidth - UiSettings.editorMinWidth - navvWidth + splitterWidths[splitter];
  }
  public editorWidth(uiEnum: UiEnum): number {
    const operatorsLeftPositionWidth = this.operatorsLeftPositionRef?.width || 0;
    const layoutContainerWidth = this.layoutContainertRef?.nativeElement?.clientWidth || 0;
    const navigationContainerWidth = this.navigationContainerRef?.nativeElement?.clientWidth || 0;
    const containerWidth = layoutContainerWidth - navigationContainerWidth - operatorsLeftPositionWidth;

    this.updateLineNumber();

    switch (uiEnum) {
      case UiEnum.editorContainer:
        return containerWidth;
      case UiEnum.editorBody:
        return containerWidth - UiSettings.lineNumberWidth;
      case UiEnum.editorTitle:
        return containerWidth + operatorsLeftPositionWidth;
      default:
        return containerWidth;
    }
  }

  private triggerSearchOnEditorChange(): void {
    const cursorPosition = this.getCursorPosition();

    if (this.navigationComponent ===  ComponentNameEnum.SearchComponent) {
      if (this.searchComponent.currentSearchText.length > 0) {
        this.handleSearchParameter({
          activeRegularExpressionButtonList: this.selectedRegExp,
          currentSearchText: this.currentSearch,
          isAllMatchActive: this.isAllMatchActive,
        });
        this.searchOccurence = this.findSearchoccurence(this.oPathEditor.innerText);

        this.setNewCursorPosition(cursorPosition);
      }
    }
  }
  public handleInput(): void {
    this.triggerSearchOnEditorChange();
    this.backupCursorPosition();
    this.saveEditorHistory();
    this.updateLineNumber();
  }

  public handleSearchParameter(searchParameter: SearchParameter): void {
    this.searchOnInit(searchParameter);
    if (searchParameter.isAllMatchActive) {
      this.executeSearch();
    } else {
      this.executeSearch();

      this.highlightMatchedText(this.matcheIndex, 'var(--PrimaryAccentColor, #f39654)');
    }
    const searchedElem = document.querySelectorAll('.highlight');
    if (searchedElem.length > 0) {
      this.editorScrollView.instance.scrollToElement(searchedElem[0]);
    }
  }

  public handleRelaceParameter(replaceParameter: { mode: string; replaceText: string }): void {
    if (
      this.oPathEditor?.textContent?.length > 0 &&
      replaceParameter.replaceText.length > 0 &&
      this.searchOccurence > 0
    ) {
      if (replaceParameter.mode === ReplaceMode.replace_all) {
        this.replaceAll(replaceParameter.replaceText);
        this.executeSearch();
      } else {
        this.replaceOne(replaceParameter.replaceText, this.matcheIndex);
        this.executeSearch();
        this.iterateMatchIndex(MatchMode.search_up);
        this.highlightMatchedText(this.matcheIndex, 'var(--PrimaryAccentColor, #f39654)');
      }
      this.saveEditorHistory();
      this.oPathEditor.blur();
    }
  }

  public handleMatchText(direction: MatchMode.search_up | MatchMode.search_down | MatchMode.search_all): void {
    if (direction === MatchMode.search_all) {
      this.executeSearch();
    } else {
      this.executeSearch();
      this.iterateMatchIndex(direction);
      this.highlightMatchedText(this.matcheIndex, 'var(--PrimaryAccentColor, #f39654)');
    }
    setTimeout(() => this.oPathEditor.blur(), 100);
  }

  public async loadNavigationComponent(componentName: ComponentNameEnum): Promise<void> {
    this.navigationComponent = componentName;
  }

  public async startOPathValidation(): Promise<void> {
    const translatedOpath: string = this.neutralDesignationActive
      ? this.editor.nativeElement.innerText
      : ConvertHelper.toString(await this.translateOpath(this.editor.nativeElement.innerText));

    this.validateComponent && (this.validateComponent.loadingValidation = true);

    this.expressionEditorService.executeValidation({
      oPath: translatedOpath.length ? translatedOpath : this.editor.nativeElement.innerText,
      objectTypes: this.objectTypeInfos,
      validationType: this.oPathValidationService.validationType,
    });
  }

  public updateLineNumber(): void {
    if (this.editor) {
      this.editorLineCount = Math.floor(this.editor.nativeElement.clientHeight / 20) - 1;
      this.lineNumbers = Array.from({ length: this.editorLineCount }, (_, index) => index + 1);
    }
  }

  private createRegularExpression(): void {
    const REGEX = {
      GENERAL: { PATTERN: this.currentSearch, FLAG: REGEX_FLAG.GENERAL },
      MATCHCASE: { PATTERN: this.currentSearch, FLAG: REGEX_FLAG.MATCHCASE },
      WOHLEWORD: { PATTERN: `\\b${this.currentSearch}\\b`, FLAG: REGEX_FLAG.WOHLEWORD },
      MATCHCASE_WOHLEWORD: { PATTERN: `\\b${this.currentSearch}\\b`, FLAG: REGEX_FLAG.MATCHCASE_WOHLEWORD },
    };
    const IS_GENERAL = this.selectedRegExp.length === 0;
    const IS_MATCHCASE = this.selectedRegExp.includes(RegExpMode.match_case);
    const IS_WHOLEWORD = this.selectedRegExp.includes(RegExpMode.whole_word);

    if (IS_GENERAL) {
      this.regex = new RegExp(`${REGEX.GENERAL.PATTERN}`, REGEX.GENERAL.FLAG);
    } else if (IS_MATCHCASE && IS_WHOLEWORD) {
      this.regex = new RegExp(`${REGEX.MATCHCASE_WOHLEWORD.PATTERN}`, REGEX.MATCHCASE_WOHLEWORD.FLAG);
    } else if (IS_MATCHCASE) {
      this.regex = new RegExp(`${REGEX.MATCHCASE.PATTERN}`, REGEX.MATCHCASE.FLAG);
    } else {
      this.regex = new RegExp(`${REGEX.WOHLEWORD.PATTERN}`, REGEX.WOHLEWORD.FLAG);
    }
  }

  public createResult(
    textExpressionEditorClone: string,
    ParameterDefinitions: ParameterDefinition[],
    validate: ValidationResult
  ): void {
    this.result = new ExpressionEditorResult();
    this.result.oPath = textExpressionEditorClone;
    this.result.parameterDefinitions = ParameterDefinitions.filter((p: ParameterDefinition) => p.IsUserCreated);
    this.result.validate = validate;
    this.result.columnTitle = this.columnTitle.currentValue.length
      ? this.columnTitle.currentValue
      : this.columnTitle.initialValue;
    this.oPathValidationService.expressionEditorResult = new ExpressionEditorResult();
    this.oPathValidationService.expressionEditorResult = this.result;
  }

  private executeSearch(): void {
    if (this.oPathEditor?.textContent?.length > 0) {
      this.createRegularExpression();
      this.getFormatedEditorContentArray();
      this.renderEditorAndhighlightSearch();
    }
  }

  private findSearchoccurence(text: string): number {
    let occurence = 0;

    let match;

    while ((match = this.regex.exec(text)) !== null) {
      occurence++;
    }
    return occurence;
  }
  private getFormatedEditorContentArray(content?: string): void {
    const editor = (content ?? this.oPathEditor.innerText).split(/[\r\n]/);

    this.editorContentArray = [];

    let line = 0;

    do {
      const CURRENT_LINE = editor[line];
      this.editorContentArray.push(CURRENT_LINE);
      line++;
    } while (line < editor.length);
    this.updateLineNumber();
  }

  public handleSelection(): void {
    this.mouseTextSelected = this.getSelectedText();
    this.backupCursorPosition();
  }

  private getSelectedText(): string {
    const selection = window.getSelection();
    if (selection && selection.toString().trim() !== '') {
      return selection.toString();
    }
    return null;
  }

  private backupCursorPosition(s?: Selection) {
    if (this.isEditorSelected(window.getSelection())) {
      const selection = s ?? window.getSelection();

      if (selection.rangeCount > 0) {
        var range = selection.getRangeAt(0).cloneRange();
        range.collapse(true);
        this.cursorPositionBackup = { node: range.startContainer, offset: range.startOffset };
      }
    }
  }
  public focusEditor(): void {
    const selection: Selection = window.getSelection();
    if (this.editor) {
      if (selection?.anchorNode?.nodeName === 'CODE' || selection?.anchorNode?.nodeName === '#text') {
        this.editor.nativeElement.focus();
      }
    }
  }

  private restoreCursorPosition() {
    if (!this.isEditorSelected(window.getSelection())) {
      this.editor.nativeElement.focus();
      const selection: Selection = window.getSelection();
      selection.removeAllRanges();
      const range = document.createRange();
      range.setStart(this.cursorPositionBackup.node, this.cursorPositionBackup.offset);
      range.collapse(true);
      selection.addRange(range);
    }
  }

  private isEditorSelected = (selection: Selection): boolean =>
    selection.anchorNode === null ||
    (selection.focusNode as HTMLElement).isContentEditable ||
    (selection.focusNode.parentElement as HTMLElement).isContentEditable;

  private handleValidateButtonClick(): void {
    if (this.navigationComponent !== ComponentNameEnum.ValidateComponent) {
      this.navigateAndStartValidation();
    } else {
      this.validateComponent.executeValidation();
    }
  }

  public handleSaveCloseButtonClick(): void {
    this.saveCloseButtonClicked = true;

    this.startOPathValidation();
  }

  private handleResetButtonClick(): void {
    this.oPathEditor.innerText = this.editorInitialValue ?? '';
    this.placeCaretAtTheEnd();
    this.history.undo = [];
    this.history.redo = [];
    this.updateLineNumber();
    this.triggerSearchOnEditorChange();
  }

  private handleUndoButtonClick(): void {
    this.getBackHistory();
    this.triggerSearchOnEditorChange();
    this.updateLineNumber();
  }

  private getCursorPosition(): number {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const clonedRange = range.cloneRange();
    clonedRange.selectNodeContents(this.oPathEditor);
    clonedRange.setEnd(range.endContainer, range.endOffset);

    return clonedRange.toString().length;
  }

  private setNewCursorPosition(cursorPosition: number) {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    for (let i = 0; i < this.oPathEditor?.childNodes.length; i++) {
      if (cursorPosition <= this.oPathEditor?.childNodes[i]?.textContent.length) {
        if (this.oPathEditor.childNodes[i].nodeName === 'SPAN') {
          range.setStart(this.oPathEditor.childNodes[i], cursorPosition - (this.currentSearch.length - 1));
          return;
        } else {
          range.setStart(this.oPathEditor.childNodes[i], cursorPosition);
          return;
        }
      }
      cursorPosition -= this.oPathEditor?.childNodes[i].textContent.length;
    }
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);
    this.oPathEditor.focus();
  }
  private getBackHistory(): void {
    if (this.history.undo.length > 1) {
      this.history.redo.push({ content: this.oPathEditor.innerText, cursorPosition: this.getCursorPosition() });
      this.oPathEditor.innerHTML = this.history.undo[this.history.undo.length - 2].content;
      this.setNewCursorPosition(this.history.undo[this.history.undo.length - 2].cursorPosition);
      this.history.undo.pop();
    } else {
      this.history.redo.push({ content: this.oPathEditor.innerText, cursorPosition: this.getCursorPosition() });
      this.history.undo.pop();
      this.oPathEditor.innerHTML = this.editorInitialValue ?? '';
      this.placeCaretAtTheEnd();
    }
  }

  private getForwardHistory(): void {
    if (this.history.redo.length) {
      this.oPathEditor.innerHTML = this.history.redo[this.history.redo.length - 1].content;
      this.setNewCursorPosition(this.history.redo[this.history.redo.length - 1].cursorPosition);
      this.saveEditorHistory();
      this.history.redo.pop();
    }
  }

  private handleRedoButtonClick(): void {
    this.getForwardHistory();
    this.triggerSearchOnEditorChange();
    this.updateLineNumber();
  }

  private async handleToggleNeutralDesignationButtonClick(): Promise<void> {
    if (this.editor.nativeElement.innerHTML.trim().length !== 0) {
      const translatedOpath: string = ConvertHelper.toString(
        await this.translateOpath(this.editor.nativeElement.innerText)
      );

      this.editor.nativeElement.innerHTML = translatedOpath;
      this.neutralDesignationActive = !this.neutralDesignationActive;

      this.editorService.setValue(new ParameterValue('neutralDesignationActive', this.neutralDesignationActive));

      this.placeCaretAtTheEnd();
      this.updateLineNumber();
    } else {
      this.neutralDesignationActive = !this.neutralDesignationActive;

      this.editorService.setValue(new ParameterValue('neutralDesignationActive', this.neutralDesignationActive));
    }
  }

  private highlightMatchedText(matchedIndex: number, color: string): void {
    const matchElement = document.getElementById(`highlight-${matchedIndex}`);
    if (matchElement) {
      matchElement.style.backgroundColor = color;
      this.editorScrollView.instance.scrollToElement(matchElement);
    }
  }

  private insertPoint(): boolean {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedNode = range.startContainer;
      const textToLeft = selectedNode.textContent.slice(0, range.startOffset);
      const firstCharToLeft = textToLeft.charAt(textToLeft.length - 1);

      return (
        (textToLeft !== '' && !/\s$/.test(firstCharToLeft) && !/[^a-zA-Z0-9]/.test(firstCharToLeft)) ||
        /[)}\]]$/.test(firstCharToLeft)
      );
    }

    return false;
  }

  private insertContentToEditor(str: string, caller: ComponentNameEnum) {
    const editor: HTMLElement = this.editor.nativeElement;
    const selection: Selection = window.getSelection();
    const nodeToInsert: Node = document.createTextNode(
      this.insertPoint() &&
        caller !== ComponentNameEnum.OperatorsComponent &&
        caller !== ComponentNameEnum.ParametersComponent
        ? '.' + str
        : str
    );

    this.restoreCursorPosition();
    this.insertTextToEditor(editor, selection, nodeToInsert);
    this.createNewSelection(selection, nodeToInsert, caller);
    this.saveEditorHistory();
    this.backupCursorPosition(selection);
  }

  private insertTextToEditor(editor: HTMLElement, selection: Selection, nodeToInsert: Node) {
    let wrapableContent = '';
    const regex = /<([^<>]+)>/;

    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      wrapableContent = range.toString().trim();

      if (wrapableContent.length) {
        nodeToInsert.textContent = nodeToInsert.textContent.replace(regex, wrapableContent);
      }
      range.deleteContents();
      range.insertNode(nodeToInsert);
    } else {
      editor.appendChild(nodeToInsert);
    }
    this.updateLineNumber();
  }

  private createNewSelection(selection: Selection, nodeToInsert: Node, caller: ComponentNameEnum) {
    const paramStart = nodeToInsert.textContent.indexOf('<');
    const paramEnd = nodeToInsert.textContent.indexOf('>') + 1;
    const newRange = document.createRange();
    newRange.setStart(
      nodeToInsert,
      paramStart > 0 && caller !== ComponentNameEnum.OperatorsComponent
        ? paramStart
        : caller === ComponentNameEnum.FunctionComponent
        ? 0
        : nodeToInsert.textContent.length
    );
    newRange.setEnd(
      nodeToInsert,
      paramStart > 0 && caller !== ComponentNameEnum.OperatorsComponent ? paramEnd : nodeToInsert.textContent.length
    );

    selection.removeAllRanges();
    selection.addRange(newRange);
  }

  private iterateMatchIndex(matchMode: MatchMode): void {
    this.searchComponent.disableAllMatchButton();
      matchMode === MatchMode.search_down 
      ? this.matcheIndex = (this.matcheIndex + 1) % this.searchOccurence
      :this.matcheIndex = (this.matcheIndex - 1 + this.searchOccurence) % this.searchOccurence;
  }

  public navigateAndStartValidation(): void {
    if (this.navigationComponent !== ComponentNameEnum.ValidateComponent) {
      this.loadNavigationComponent(ComponentNameEnum.ValidateComponent);
      this.navComponent.loadComponent(
        this.navComponent.navigationEntries.find((_) => _.componentName === ComponentNameEnum.ValidateComponent)
      );
      this.startOPathValidation();
    }
  }

  private renderEditorAndhighlightSearch(): void {
    this.oPathEditor.innerHTML = '';
    this.searchOccurence = 0;
    if (this.currentSearch.length === 0) {
      this.editorContentArray.map((line, index) => {
        let lineNode: string = '';
        lineNode = `${line}${index < this.editorContentArray.length - 1 ? `\n` : ''}`;
        this.oPathEditor.innerHTML += lineNode;
      });
    } else {
      this.createRegularExpression();
      let idCounter = 0;

      this.editorContentArray.map((line, index) => {
        this.searchOccurence += this.findSearchoccurence(line);
        let lineNode: string = '';
        line = line.replace(this.regex, (match) => {
          const spanId = `highlight-${idCounter++}`;
          return `<span  contenteditable="true" class="highlight" id="${spanId}">${match}</span>`;
        });
        lineNode = `${line}${index < this.editorContentArray.length - 1 ? `\n` : ''}`;
        this.oPathEditor.innerHTML += lineNode;
      });

      const style = document.createElement('style');
      style.innerHTML = `.highlight {    background-color: yellow ;  }`;
      document.head.appendChild(style);
    }
  }

  private replaceAll(newText: string): void {
    const content = this.oPathEditor.innerHTML;
    const REPLACE_ALL_REGEX = new RegExp(`<span[^>]*\\s+id="highlight[^"]*"[^>]*>(.*?)<\\/span>`, 'g');
    let replacedContent = '';

    replacedContent = content.replace(REPLACE_ALL_REGEX, () => newText);

    this.oPathEditor.innerHTML = replacedContent;
  }

  private replaceOne(newText: string, spanId: number): void {
    const content = this.oPathEditor.innerHTML;
    const matchElement = document.getElementById(`highlight-${spanId}`);
    this.editorScrollView.instance.scrollToElement(matchElement);
    const REPLACE_ONE_REGEX = new RegExp(`<span[^>]*\\s+id="highlight-${spanId}"[^>]*>(.*?)<\\/span>`, 'g');
    let replacedContent = '';
    replacedContent = content.replace(REPLACE_ONE_REGEX, () => newText);
    this.searchOccurence = this.findSearchoccurence(replacedContent);
    this.oPathEditor.innerHTML = replacedContent;
  }

  private saveEditorHistory(): void {
    if (this.oPathEditor) {
      this.history.undo.push({
        content: this.oPathEditor.innerText,
        cursorPosition: this.getCursorPosition(),
      });
    }
  }

  private searchOnInit(searchParameter?: SearchParameter): void {
    this.matcheIndex = 0;
    this.searchOccurence = 0;
    this.currentSearch = '';
    this.selectedRegExp = [];
    if (searchParameter) {
      this.currentSearch = searchParameter.currentSearchText;
      this.selectedRegExp = searchParameter.activeRegularExpressionButtonList;
      this.isAllMatchActive = searchParameter.isAllMatchActive;
    }

    this.oPathEditor.scrollTop;
  }

  private async translateOpath(opath: string, forceNeutral?: boolean): Promise<ArrayBuffer | void> {
    const queryExpression = new QueryExpressionModel();
    queryExpression.ObjectType = this.objectTypeInfo.name;
    queryExpression.OPath = opath;

    return forceNeutral
      ? !this.neutralDesignationActive
        ? this.coreDataService
          .executeLocalizeExpressionQuery(queryExpression)
          .pipe(takeUntil(this.unsubscribe$))
          .toPromise()
          .catch(() => this.navigateAndStartValidation())
        : null
      : this.neutralDesignationActive
        ? this.coreDataService
          .executeLocalizeExpressionQuery(queryExpression)
          .pipe(takeUntil(this.unsubscribe$))
          .toPromise()
          .catch(() => this.navigateAndStartValidation())
        : this.coreDataService
          .executeUnlocalizeExpressionQuery(queryExpression)
          .pipe(takeUntil(this.unsubscribe$))
          .toPromise()
          .catch(() => this.navigateAndStartValidation());
  }

  public togglePermanentNavigation(componentName :ComponentNameEnum ): void {
    this.permanentNavigation[componentName] = !this.permanentNavigation[componentName]
  }

}
